import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Check, CreditCard, Building2, Download } from 'lucide-react';
import { Button as CommonButton } from '../../../../ui/components/Common/Buttons/Button';
import { InsightCard } from '../components';
import {
  INSIGHT_CARDS,
  LICENSE_DISTRIBUTION,
  OVERVIEW_METRIC_TILES,
  OVERVIEW_TIMEFRAMES,
  TOTAL_JOBS_CREATED,
} from '../constants';
import { toUsagePercent } from '../functions';
import { useOverviewTab } from '../hooks';

export const OverviewTab: React.FC = () => {
  const overview = useOverviewTab();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'repeat(12, 1fr)' },
        gap: 4,
      }}
    >
      {/* Main Content Column */}
      <Box sx={{ gridColumn: { lg: 'span 8' }, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Usage Analytics Chart */}
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { md: 'center' },
              gap: 2,
              mb: 4,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
                Usage Analytics
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mt: 0.5 }}
              >
                Resource consumption across active services
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={overview.timeframe}
              exclusive
              onChange={(_, val) => {
                if (val) overview.setTimeframe(val);
              }}
              size="small"
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 2,
                '& .MuiToggleButton-root': {
                  textTransform: 'uppercase',
                  fontWeight: 800,
                  fontSize: '0.625rem',
                  letterSpacing: '0.05em',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                },
              }}
            >
              {OVERVIEW_TIMEFRAMES.map((timeframe) => (
                <ToggleButton key={timeframe} value={timeframe}>
                  {timeframe}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ height: 350, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={overview.chartData}>
                <defs>
                  <linearGradient id="colorHT" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorCB" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorAS" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: '16px',
                    border: 'none',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                />
                <Legend
                  iconType="circle"
                  wrapperStyle={{
                    paddingTop: '20px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="hierTalent"
                  name="HierTalent Base"
                  stroke="#10b981"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorHT)"
                />
                <Area
                  type="monotone"
                  dataKey="candiBank"
                  name="CandiBank"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorCB)"
                />
                <Area
                  type="monotone"
                  dataKey="assessments"
                  name="Auto Assessments"
                  stroke="#f59e0b"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorAS)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        {/* Operational Insights Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
                Operational Insights
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mt: 0.5 }}
              >
                Detailed performance metrics across hiring funnels
              </Typography>
            </Box>
            <Button
              startIcon={<Download size={14} />}
              variant="outlined"
              size="small"
              sx={{
                textTransform: 'uppercase',
                fontWeight: 800,
                fontSize: '0.625rem',
                letterSpacing: '0.05em',
                borderRadius: 2,
              }}
            >
              Export Report
            </Button>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            {/* JOs Created Per Month */}
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 4,
                gridColumn: { md: 'span 2' },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}
                >
                  Job Openings Created (Last 6 Months)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: 'primary.main' }}>
                    {TOTAL_JOBS_CREATED}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled' }}>
                    TOTAL
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ height: 192, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overview.jobCreationData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f8fafc" />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                    />
                    <Tooltip
                      cursor={{ fill: '#f8fafc' }}
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {overview.jobCreationData.map((_entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={index === overview.jobCreationData.length - 1 ? '#10b981' : '#e2e8f0'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            {INSIGHT_CARDS.map((card) => (
              <InsightCard
                key={card.title}
                title={card.title}
                metrics={card.metrics}
                color={card.color}
              />
            ))}
          </Box>
        </Box>

        {/* Metrics Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
          {OVERVIEW_METRIC_TILES.map((metric) => (
            <Paper
              key={metric.label}
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 4,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 2 },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 3,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                  }}
                >
                  <svg style={{ width: 20, height: 20 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={metric.iconPath} />
                  </svg>
                </Box>
                <Chip
                  label={metric.change}
                  size="small"
                  color={metric.trend === 'up' ? 'success' : 'error'}
                  sx={{ fontWeight: 800, fontSize: '0.625rem', height: 20, borderRadius: 1.5 }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}
              >
                {metric.label}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: '-0.02em' }}>
                {metric.value}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* Contact Information & General Details */}
        <Paper variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
          <Box
            sx={{
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
              Contact Information
            </Typography>
            <Button size="small" sx={{ fontWeight: 700, textTransform: 'none', color: 'primary.main' }}>
              Edit
            </Button>
          </Box>
          <Box
            sx={{
              p: 4,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 4,
            }}
          >
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 1.5 }}
                >
                  Primary Administrator
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.light', color: 'primary.contrastText', fontWeight: 700 }}>
                    JD
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Jane Doe
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      jane.doe@acmeglobal.com
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 1 }}
                >
                  Corporate Address
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, lineHeight: 1.6 }}>
                  101 Innovation Way, Suite 500
                  <br />
                  San Francisco, CA 94103, USA
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 1 }}
                >
                  Billing Email
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                  accounts@acmeglobal.com
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mt: 0.5 }}>
                  +1 (555) 902-1231
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Paper>

        {/* License Distribution */}
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em', mb: 4 }}>
            License Distribution
          </Typography>
          <Stack spacing={3}>
            {LICENSE_DISTRIBUTION.map((item) => (
              <Box key={item.label}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {item.total ? `${item.used} / ${item.total}` : item.status}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={item.total && item.used ? toUsagePercent(item.used, item.total) : 100}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: item.color === 'blue' ? 'info.main' : item.color === 'amber' ? 'warning.main' : 'primary.main',
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Paper>
      </Box>

      {/* Sidebar Column */}
      <Box sx={{ gridColumn: { lg: 'span 4' }, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Subscription Status */}
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em', mb: 3 }}>
            Subscription Status
          </Typography>
          <Stack spacing={2.5}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: 'rgba(16, 185, 129, 0.04)',
                borderColor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: 'rgba(16, 185, 129, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                }}
              >
                <Check size={18} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Status: Active
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Renewal: Oct 12, 2024
                </Typography>
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: 'rgba(59, 130, 246, 0.04)',
                borderColor: 'info.light',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: 'rgba(59, 130, 246, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'info.main',
                }}
              >
                <CreditCard size={18} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Annual Billing
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  $12,500.00 / year
                </Typography>
              </Box>
            </Paper>

            <CommonButton
              label="View Billing History"
              variant="secondary"
              sx={{ width: "100%", borderRadius: 4, py: 1.5, fontWeight: 700, color: "#059669", borderColor: "#a7f3d0", "&:hover": { bgcolor: "rgba(236, 253, 245, 0.5)" } }}
            />
          </Stack>
        </Paper>

        {/* Branding Guidelines */}
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em', mb: 3 }}>
            Branding Guidelines
          </Typography>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 1.5 }}
              >
                Primary Color
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 48, height: 32, borderRadius: 2, bgcolor: '#00A651', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)' }} />
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '0.05em' }}>
                  #00A651
                </Typography>
              </Stack>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 1.5 }}
              >
                Logo (Dark Theme)
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  height: 96,
                  bgcolor: '#0f172a',
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  p: 2,
                }}
              >
                <Building2 size={28} color="#00A651" />
                <Typography variant="h6" sx={{ fontWeight: 900, color: '#ffffff', letterSpacing: '-0.02em' }}>
                  ACME GLOBAL
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};
